/**
 * Canonical GPX brand color scales. Import here and in Tailwind / PrimeVue only from this file
 * so hex values stay in one place. See docs/design-tokens-and-theming.md.
 */

/** Matches `apps/app` Tailwind `gpx_gray` — neutrals for lists, borders, secondary text. */
export const gpxGray = {
  100: '#6C7789',
  200: '#818C9C',
  300: '#98A1AE',
  400: '#AFB6C0',
  500: '#C0C5CE',
  600: '#D1D5DB',
  700: '#DDE0E4',
  800: '#E8EAED',
  900: '#F4F5F6',
}

/** Matches `apps/app` Tailwind `gpx_slate` 100 — used in dark `surface` ramp. */
export const gpxSlate100 = '#49576E'

/** Matches `apps/app` Tailwind `gpx_black` — dark chrome / inverse text. */
export const gpxBlack = {
  50: '#1A1D23',
  100: '#1D222A',
  200: '#212731',
  300: '#232B38',
  400: '#323B48',
  500: '#3C4553',
}

/** CTA terracotta steps (matches Tailwind `gpx_terracotta` 100–300). */
export const gpxTerracottaCta = {
  100: '#E0352E',
  200: '#E24640',
  300: '#E55852',
}

/** Primary purple scale (matches former apps/app tailwind `gpx_porto`). */
export const gpxPorto = {
  100: '#49396A',
  200: '#524077',
  300: '#5A4587',
  400: '#6C53A2',
  500: '#8069B5',
  600: '#9782C4',
  700: '#AD9CD3',
  800: '#CABFE3',
  900: '#E8E2F3',
}

/**
 * Default seed for PrimeVue `semantic.primary` — same as **`gpxTerracottaCta[300]`**
 * and Tailwind/UI CTA (`btn.accent` / `:root --btn-accent`).
 */
export const brandCtaPrimaryHex = gpxTerracottaCta[300]

/** Legacy name: Porto purple step used for `gpx_porto` in Tailwind (not the CTA accent). */
export const brandPrimaryHex = gpxPorto[500]

/**
 * Button colors for PrimeVue `gpx-theme` — derived from the same CTA scale as `brandCtaPrimaryHex`.
 * Keep app `:root` `--btn-*` in sync (see `docs/design-tokens-and-theming.md`).
 */
export const gpxButtonThemeColors = {
  accent: brandCtaPrimaryHex,
  accentHover: gpxTerracottaCta[200],
  accentText: gpxTerracottaCta[100],
  secondaryBg: '#5a6b87',
  secondaryHoverBg: '#4d5c73',
  secondaryText: '#ffffff',
  tertiaryBorder: '#ced4de',
  tertiaryText: '#49576e',
  tertiaryHoverBg: gpxGray[900],
}

/**
 * PrimeVue Aura `semantic.colorScheme.*.surface` — maps GPX neutrals to `--p-surface-*`.
 * Light: lightest backgrounds at low steps; 900/950 for strong text (see Prime surface docs).
 */
export const gpxPrimeSurfaceLight = {
  0: '#ffffff',
  50: gpxGray[900],
  100: gpxGray[800],
  200: gpxGray[700],
  300: gpxGray[600],
  400: gpxGray[500],
  500: gpxGray[400],
  600: gpxGray[300],
  700: gpxGray[200],
  800: gpxGray[100],
  900: gpxBlack[500],
  950: gpxBlack[100],
}

/**
 * Dark mode surfaces — ramp from page bg to light borders (GPX `gpx_black` + gray).
 */
export const gpxPrimeSurfaceDark = {
  0: gpxBlack[50],
  50: gpxBlack[100],
  100: gpxBlack[200],
  200: gpxBlack[300],
  300: gpxBlack[400],
  400: gpxBlack[500],
  500: gpxSlate100,
  600: gpxGray[100],
  700: gpxGray[300],
  800: gpxGray[500],
  900: gpxGray[700],
  950: gpxGray[900],
}
